import { useState } from "react";
import { heatTabs, hotSearchWords } from "@/mocks/gossipData";
import { Chip, SectionHead, TrendIcon } from "./ui";

export default function HeatBoard() {
  const [active, setActive] = useState("today");
  const tab = heatTabs.find((t) => t.id === active) ?? heatTabs[0];
  const rankCls = (i: number) => {
    if (i === 0) return "text-accent-500";
    if (i === 1) return "text-primary-500";
    if (i === 2) return "text-secondary-500";
    return "text-foreground-300";
  };

  return (
    <section id="heat" className="scroll-mt-24 bg-background-50">
      <div className="mx-auto max-w-[1280px] px-4 py-14 md:px-6">
        <SectionHead
          index="01"
          kicker="热度总榜 · 按讨论增量排序"
          title="今日热议 · 谁在风口浪尖"
          desc="热度不代表对错，只代表讨论量。我们把置顶讨论按增量实时汇总，想看反转与澄清也点进去留意时间线。"
          right={
            <div className="inline-flex items-center gap-1 rounded-full border border-background-200 bg-background-100 px-1 py-1">
              {heatTabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActive(t.id)}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition ${
                    active === t.id ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          }
        />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-100/60">
            <div className="flex flex-col divide-y divide-background-200">
              {tab.items.map((it, i) => {
                const top3 = i < 3;
                return (
                  <a
                    key={`${tab.id}-${it.title}`}
                    href="#flip"
                    className={`group flex flex-col gap-2 px-4 py-3.5 transition hover:bg-background-50 sm:flex-row sm:items-center sm:gap-4 sm:px-5 ${
                      top3 ? "bg-accent-50/40" : ""
                    }`}
                  >
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md font-heading text-lg font-black ${
                        top3 ? "bg-accent-100" : "bg-background-200"
                      } ${rankCls(i)}`}
                    >
                      {i + 1}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="text-[15px] font-bold leading-snug text-foreground-900 group-hover:text-primary-700">
                          {it.title}
                        </span>
                        <Chip tone={top3 ? "accent" : "plain"}>{it.tag}</Chip>
                      </span>
                      <span className="mt-1 block text-sm text-foreground-600">{it.abstract}</span>
                    </span>
                    <span className="flex shrink-0 items-center justify-end gap-3 sm:w-28">
                      <TrendIcon trend={it.trend} />
                      <span className="text-sm font-extrabold text-foreground-700">{it.heat}</span>
                    </span>
                  </a>
                );
              })}
            </div>
            <div className="flex items-center justify-between border-t border-background-200 bg-background-50 px-5 py-2.5 text-xs text-foreground-400">
              <span>每小时刷新 · 含热议、争议、反转三类标记</span>
              <a href="#topic" className="inline-flex items-center gap-1 font-semibold text-primary-600 hover:text-primary-700">
                查看热议话题 <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>

          <aside className="flex flex-col gap-5">
            <div className="rounded-lg border border-background-200 bg-background-100/60 p-4">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-foreground-950 text-accent-400">
                  <i className="ri-search-eye-line text-sm"></i>
                </span>
                <h3 className="font-heading text-base font-extrabold text-foreground-950">搜索热词 TOP6</h3>
              </div>
              <div className="mt-3 flex flex-col gap-1">
                {hotSearchWords.map((h) => (
                  <a
                    key={h.word}
                    href="#heat"
                    className="flex items-center justify-between rounded-md px-2 py-1.5 text-sm hover:bg-background-50"
                  >
                    <span className="flex items-center gap-2 text-foreground-700">
                      <span className="w-3 text-xs font-bold text-foreground-400">{h.rank}</span>
                      <span className="font-medium">{h.word}</span>
                    </span>
                    <span className="flex items-center gap-1.5 text-[11px] text-foreground-400">
                      <span className={`${h.up ? "text-primary-500" : "text-secondary-500"}`}>
                        <i className={`${h.up ? "ri-arrow-up-line" : "ri-arrow-down-line"} text-xs`}></i>
                      </span>
                      {h.heat}
                    </span>
                  </a>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-primary-200 bg-primary-50/70 p-4">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className="ri-voiceprint-line text-sm"></i>
                </span>
                <h3 className="font-heading text-base font-extrabold text-foreground-950">关于这份榜</h3>
              </div>
              <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">
                争议不等于有罪，热度也不等于真相。榜单只做讨论量的客观呈现，是非对错请你综合多方信源自行判断——这是这份底账唯一的态度。
              </p>
              <a
                href="#guard"
                className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-primary-700 hover:text-primary-800"
              >
                查看圈内守则 <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}